/**
 * How ROUTER_REPORT splits for the unpacking animation.
 *
 * The CSS hardcodes `width: 5ch` and `steps(5)` against the length of the
 * hidden runs, so these are not free-form strings -- changing one without
 * the other clips a character or stalls the reveal. The test alongside this
 * file is what keeps the two in agreement.
 */
export const WORDMARK_FIXED_1 = "R";
export const WORDMARK_GROW_1 = "OUTER";
export const WORDMARK_SEPARATOR = "_";
export const WORDMARK_FIXED_2 = "R";
export const WORDMARK_GROW_2 = "EPORT";

/** What the CSS animates each hidden run to, in characters. */
export const WORDMARK_GROW_LENGTH = 5;

/** Milliseconds before the first character appears (fade-in, then a beat). */
export const WORDMARK_TYPE_START_MS = 820;

/** Milliseconds between characters. */
export const WORDMARK_CHAR_STEP_MS = 124;

/** When the last character of a run lands. */
export const WORDMARK_TYPE_END_MS =
  WORDMARK_TYPE_START_MS + (WORDMARK_GROW_LENGTH - 1) * WORDMARK_CHAR_STEP_MS;

/** Delay for the character at `index` within a run. Both runs share these. */
export function wordmarkCharDelayMs(index: number): number {
  return WORDMARK_TYPE_START_MS + index * WORDMARK_CHAR_STEP_MS;
}

/** How long the cursor lingers after the last character lands. */
export const WORDMARK_CURSOR_LINGER_MS = 160;

/** How long the cursor takes to fade once it has lingered. */
export const WORDMARK_CURSOR_FADE_MS = 300;

/** When the cursor starts fading: after the last character, plus the linger. */
export const WORDMARK_CURSOR_FADE_START_MS =
  WORDMARK_TYPE_END_MS + WORDMARK_CURSOR_LINGER_MS;

/**
 * Transient cursor trailing the second run while it types.
 *
 * The separator already trails the first run, so that word reads as being
 * typed with a cursor chasing it. The second run has nothing behind it and
 * reads as letters merely appearing -- the halves look different even
 * though they are geometrically symmetric. This is the same glyph so both
 * read identically, and it occupies no width outside the typing window, so
 * the collapsed form stays exactly R_R and the finished one exactly
 * ROUTER_REPORT.
 */
export const WORDMARK_CURSOR = WORDMARK_SEPARATOR;

/**
 * Width reserved for each half, in characters.
 *
 * The separator only stays put if both halves are the same fixed width: the
 * left one right-aligned so it grows leftward, the right one left-aligned so
 * it grows rightward. Letting the row size itself to its content instead
 * makes the separator drift by half a character whenever the cursor appears
 * or disappears, because the cursor lands on one side of the centre only.
 *
 * Must be at least as wide as the widest half, which is the right one:
 * its R, its five typed characters, and the cursor.
 */
export const WORDMARK_HALF_WIDTH = 7;

/** Widest the left half ever gets: its R plus the characters that type in. */
export const WORDMARK_LEFT_MAX = WORDMARK_FIXED_1.length + WORDMARK_GROW_1.length;

/** Widest the right half ever gets: the left max plus the transient cursor. */
export const WORDMARK_RIGHT_MAX =
  WORDMARK_FIXED_2.length + WORDMARK_GROW_2.length + WORDMARK_CURSOR.length;

/** The collapsed state, before anything slides out. */
export const WORDMARK_COLLAPSED =
  WORDMARK_FIXED_1 + WORDMARK_SEPARATOR + WORDMARK_FIXED_2;

/** The finished wordmark. */
export const WORDMARK_FULL =
  WORDMARK_FIXED_1 +
  WORDMARK_GROW_1 +
  WORDMARK_SEPARATOR +
  WORDMARK_FIXED_2 +
  WORDMARK_GROW_2;
